import {
    DataTypes as DT,
    TimeScales as TS,
    SlidingTimeScales as STS,
    ColumnSortingT,
    ColumnWidthT,
} from 'business/definitions';
import { NUMERIC_TYPES, UNKNOWN_DATUM } from 'business/constants';
import {
    dateFormatter,
    currencyFormatter,
    percentageFormatter,
    categoryFormatter,
    monthFormatter,
    quarterFormatter,
    yearFormatter,
    floatFormatter,
    integerFormatter,
    schoolYearFormatter,
    ValueFormatterT,
    KeyCreatorT,
    timeFormatter,
} from 'ui/AgGridReact/formatters';
import { translateBlockValue } from 'i18n';
import { ComparatorT } from 'ui/AgGridReact/comparators';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { formatSlidingWeek, formatSlidingYear } from 'helpers/formatHelper';
import { getYearlyComparisonSettingsForScale } from 'helpers/yearlyComparison/settings';
import { isYearlyComparisonScale } from 'helpers/yearlyComparison/scale';
import AggregationRowCellRenderer from './CellRenderers/AggregationRowCellRenderer';

export type ColDefWithField = WithRequired<ColDef, 'field'>;

export const getValueFormatter = (
    colType: DataTypesT,
    scale?: TimeScalesT | SlidingTimeScalesT | null,
): ValueFormatterT => {
    switch (colType) {
        case DT.money:
            return currencyFormatter;
        case DT.fraction:
            return percentageFormatter;
        case DT.date: {
            switch (scale) {
                case TS.month:
                    return monthFormatter;
                case TS.year:
                    return yearFormatter;
                case TS.quarter:
                    return quarterFormatter;
                case TS.schoolYear:
                    return schoolYearFormatter;
                case STS.slidingWeek:
                    return (params) => formatSlidingWeek(params.value);
                case STS.slidingYear:
                    return (params) => formatSlidingYear(params.value);
                default:
                    return dateFormatter;
            }
        }
        case DT.datetime:
            return dateFormatter;
        case DT.time:
            return timeFormatter;
        case DT.float:
            return floatFormatter;
        case DT.integer:
            return integerFormatter;
        case DT.category:
            return categoryFormatter;

        default:
            return (params) =>
                params.value === UNKNOWN_DATUM ? '-' : params.value;
    }
};

const getKeyCreator = (colType: DataTypesT): KeyCreatorT => {
    switch (colType) {
        case DT.category:
            return categoryFormatter;
        case DT.year:
        case DT.integer:
        case DT.date:
        case DT.datetime:
        case DT.text:
        case DT.tree:
        case DT.float:
        case DT.money:
        case DT.fraction:
            return ({ value }) => value;
        default:
            return () => '';
    }
};

const getColumnComparator = (columnKey: string): ComparatorT | undefined => {
    if (isYearlyComparisonScale(columnKey)) {
        const yearlyComparisonSettings =
            getYearlyComparisonSettingsForScale(columnKey);
        return yearlyComparisonSettings.sort;
    }
    return undefined;
};

const pivotByDefault = ['budgetLine.year', 'defaultTime', 'year'];
const rowGroupByDefault = [
    'nature.number',
    'nature.label',
    'chapter.code',
    'chapter.label',
    'antenna.code',
    'antenna.name',
    'function.number',
    'function.label',
    'budgetLine.expenseIncome',
    'orgChart.name',
    'orgChartFinance.name',
    'orgChartFinance.code',
    'orgChartHr.name',
    'orgChartHr.code',
    'gestionnaire.name',
];

const getAggFunc = (column: ResponseColumnT): string | undefined => {
    switch (column.type) {
        case DT.fraction:
            return 'fractionAggFunc';
        case DT.year:
            return undefined;
        default:
            return 'sum';
    }
};

const getPivotGridColDef = (column: ResponseColumnT): ColDef => {
    const valueFormatter = getValueFormatter(column.type, column.scale);
    const headerName = translateBlockValue(column.key, true);
    const keyCreator = getKeyCreator(column.type);
    const pivotComparator = getColumnComparator(column.key);
    if (rowGroupByDefault.includes(column.key)) {
        return {
            field: column.key,
            valueFormatter,
            keyCreator,
            rowGroup: true,
            enableRowGroup: true,
            pivot: false,
            enablePivot: true,
            enableValue: false,
            headerName,
            pivotComparator,
        };
    }
    if (pivotByDefault.includes(column.key)) {
        return {
            field: column.key,
            valueFormatter,
            keyCreator,
            rowGroup: false,
            enableRowGroup: true,
            pivot: true,
            enablePivot: true,
            enableValue: false,
            headerName,
            pivotComparator,
        };
    }
    if (NUMERIC_TYPES.includes(column.type)) {
        return {
            field: column.key,
            valueFormatter,
            keyCreator,
            rowGroup: false,
            enableRowGroup: true,
            pivot: column.type === DT.year,
            enablePivot: true,
            enableValue: column.type !== DT.year,
            aggFunc: getAggFunc(column),
            headerName,
            pivotComparator,
        };
    }
    return {
        field: column.key,
        keyCreator,
        valueFormatter,
        rowGroup: !column.isMantyId,
        enableRowGroup: true,
        pivot: false,
        enablePivot: true,
        enableValue: false,
        headerName,
        comparator: pivotComparator,
        pivotComparator,
        initialHide: column.isMantyId,
    };
};

export const columnOrder = [
    // budget preparation
    'budgetLine.amountRequested',
    'budgetLine.amountArbitrated1',
    'budgetLine.amountVoted',
    'budgetLine.amountVotedSup',
    'budgetLine.amountReported',
    'budgetLine.amountAmendingDecision',
    'budgetLine.amountSpecialAuthorization',
    'budgetLine.fullAmount',
    // budget execution
    'budgetLine.amountEngaged',
    'budgetLine.percentEngaged',
    'budgetLine.amountMandated',
    'budgetLine.amountEngagedUnpaid',
    'budgetLine.amountCleared',
    'budgetLine.percentMandated',
    'budgetLine.amountRealized',
    'budgetLine.percentRealized',
    'mandate.amountAdminAccount',
    'budgetLine.amountAvailable',
    'budgetLine.amountRattached',
    // ventilated amounts
    'budgetLineAnalyticBreakdown.amount',
    'mandateAnalyticBreakdown.amount',
    // market
    'market.amountNoTax',
    'market.amountTax',
    'market.amountFullTax',
    'market.amountEngaged',
    'market.amountMandated',
    // market lot
    'marketLot.startDate',
    'marketLot.endDate',
    'marketLot.notificationDate',
    'marketLot.duration',
    'marketLot.priceForm',
    'marketLot.label',
    'marketLot',
    'marketLot.naming',
    'marketLot.lotNumber',
    'marketLot.objective',
    'marketLot.lotObject',
    'marketLot.timeEvolution',
    'marketLot.priceType',
    'marketLot.amountNoTax',
    'marketLot.amountNoTaxAmendment',
    'marketLot.reviewAmountNoTax',
    'marketLot.amountNoTaxReviewed',
    'marketLot.amountRemainingNoTax',
    'marketLot.amountEngagedHt',
    'marketLot.amountMandatedHt',
    'marketLot.amountAvailableNoTax',
    'marketLot.amountFullTax',
    'marketLot.reviewAmountFullTax',
    'marketLot.amountFullTaxReviewed',
    'marketLot.amountRemaining',
    'marketLot.amountEngagedTtc',
    'marketLot.amountEngaged',
    'marketLot.amountMandatedTtc',
    'marketLot.amountMandated',
    'marketLot.amountAvailable',
    'marketLot.amountTax',
    // engagement
    'engagement.amountNoTax',
    'engagement.amountTax',
    'engagement.amountFullTax',
    'engagement.amountAvailable',
    'engagement.amountMandated',
    'engagement.percentMandated',
    'engagement.amountAdminAccount',
    // DGFIP indicators
    'dgfipIndicators.budgeted',
    'dgfipIndicators.realized',
    'dgfipIndicators.realizedRate',
    // invoice professional
    'invoiceProfessional.receptionCreationDuration',
    'invoiceProfessional.receptionSentToServiceDuration',
    'invoiceProfessional.receptionMandateDuration',
    'invoiceProfessional.receptionPaymentDuration',
    'invoiceProfessional.creationServiceDoneDuration',
    'invoiceProfessional.sentToServiceVisaDuration',
    'invoiceProfessional.visaLiquidationDuration',
    'invoiceProfessional.visaMandateDuration',
    'invoiceProfessional.liquidationMandateDuration',
    'invoiceProfessional.mandatePaymentDuration',
    'invoiceProfessional.treatmentDuration',
];

/** "smart" TCD system: determines for each field wether it should a rowGroup, pivot, or metric by default */
export const getDefaultPivotGridColumns = (
    columns: ResponseColumnT[],
    isSupportUser: boolean = false,
): ColDef[] => {
    const colDefs = columns
        // Filter out columns that are MantyId unless the user is a support user
        .filter((col) => !col.isMantyId || isSupportUser)
        .map(getPivotGridColDef);
    // NOTE - AgGrid doesn't allow us to track the order of value columns, and hence
    // we are not able to save the order of value columns.
    // To work around this, we hard code an order which makes sens business wise
    // see the support request we made here: https://ag-grid.zendesk.com/hc/en-us/requests/20180
    return colDefs.sort((a, b) => {
        const aIndex = columnOrder.indexOf(a.field || '');
        const bIndex = columnOrder.indexOf(b.field || '');
        return aIndex - bIndex;
    });
};

const AGG_NUMERIC_TYPES: readonly DataTypesT[] = [
    DT.integer,
    DT.float,
    DT.money,
    DT.fraction,
];

const isColumnCanBeAggregate = (column: ResponseColumnT): boolean =>
    Boolean(AGG_NUMERIC_TYPES.includes(column.type));

const getAgFilter = (column: ResponseColumnT): ColDefWithField['filter'] => {
    switch (column.type) {
        case DT.money:
        case DT.float:
        case DT.integer:
        case DT.fraction:
        case DT.year:
            return 'agNumberColumnFilter';
        case DT.date:
        case DT.datetime:
            return 'agDateColumnFilter';
        default:
            return 'agTextColumnFilter';
    }
};

const getFloatingFilterComponent = (
    column: ResponseColumnT,
): ColDefWithField['floatingFilterComponent'] => {
    switch (column.type) {
        case DT.money:
        case DT.float:
        case DT.integer:
        case DT.fraction:
        case DT.year:
            return 'numberFloatingFilter';
        case DT.category:
        case DT.text:
        case DT.tree:
        case DT.table:
            return 'textFloatingFilter';
        default:
            return undefined;
    }
};

const getFilterValueGetterByColumnType = (
    column: ResponseColumnT,
    valueFormatter: ValueFormatterT,
): ColDefWithField['filterValueGetter'] => {
    const columnKey = String(column.key);

    return (params: ValueGetterParams) => {
        const value = params.getValue(columnKey);
        if (value === UNKNOWN_DATUM) return null;
        switch (column.type) {
            case DT.text:
            case DT.category:
            case DT.tree:
                return valueFormatter({ value });
            case DT.fraction:
                return value * 100;
            default:
                return value;
        }
    };
};

const getTableColDef = (
    column: ResponseColumnT,
    hideFilter: boolean = false,
): ColDefWithField => {
    const valueFormatter = getValueFormatter(column.type, column.scale);
    const columnKey = String(column.key);
    const colDef: ColDefWithField = {
        field: columnKey,
        headerName: translateBlockValue(column.key, true),
        valueFormatter,
        valueGetter: (params) => {
            if (!params.data) {
                return null;
            }
            const value = params.data[columnKey];
            if (value === UNKNOWN_DATUM) return null;
            return value;
        },
        cellRendererSelector: (params) => {
            if (params.node.rowPinned) {
                return {
                    component: AggregationRowCellRenderer,
                    params: {
                        dataType: column.type,
                        context: params.context,
                    },
                };
            }
            return undefined;
        },
        floatingFilter: !hideFilter,
        floatingFilterComponent: getFloatingFilterComponent(column),
        filter: getAgFilter(column),
        filterValueGetter: getFilterValueGetterByColumnType(
            column,
            valueFormatter,
        ),
        initialHide: column.isMantyId,
    };

    if (isColumnCanBeAggregate(column)) {
        colDef.aggFunc =
            column.type === DT.fraction ? 'fractionAggFunc' : 'sum';
    }

    return colDef;
};

export const getFormattedTableColumns = (
    columns: ResponseColumnT[],
    hideFilter: boolean = false,
    isSupportUser: boolean = false,
): ColDefWithField[] =>
    columns
        // Filter out columns that are MantyId unless the user is a support user
        .filter((col) => !col.isMantyId || isSupportUser)
        .map((col) => getTableColDef(col, hideFilter));

export const getEnrichedColumnWithColumnsHidden = (
    columns: ColDefWithField[],
    hiddenColumns: string[] | undefined,
): ColDefWithField[] => {
    const isHiddenColumnsUndefined =
        hiddenColumns === undefined || hiddenColumns.length === 0;
    if (isHiddenColumnsUndefined) return columns;

    return columns.map((col) => {
        if (hiddenColumns.includes(col.field))
            return { ...col, initialHide: true };
        return col;
    });
};

const areAllElementsInArray = (elements: string[], array: string[]): boolean =>
    elements.every((e) => array.includes(e));

export const getEnrichedColumnWithColumnsSorting = (
    columns: ColDefWithField[],
    sortedColumns: ColumnSortingT[] | undefined,
): ColDefWithField[] => {
    if (sortedColumns === undefined) return columns;

    const isAllSortedColumnsInColumns = areAllElementsInArray(
        sortedColumns.map((c) => c.columnName),
        columns.map((c) => c.field),
    );

    if (!isAllSortedColumnsInColumns) return columns;

    return columns.map((col) => {
        const sortedColumnIndex = sortedColumns.findIndex(
            (column) => column.columnName === col.field,
        );

        if (sortedColumnIndex === -1) return col;
        return {
            ...col,
            comparator: getColumnComparator(col.field),
            initialSort: sortedColumns[sortedColumnIndex].direction,
            initialSortIndex: sortedColumnIndex,
        };
    });
};

export const getEnrichedColumnWithColumnsWidth = (
    columns: ColDefWithField[],
    columnsWidth: ColumnWidthT[] | null | undefined,
): ColDefWithField[] => {
    if (!columnsWidth) return columns;

    return columns.map((col) => {
        const sortedColumn = columnsWidth.find(
            (column) => column.columnName === col.field,
        );

        if (sortedColumn === undefined) return { ...col, initialFlex: 1 };

        return {
            ...col,
            initialWidth: sortedColumn.width,
        };
    });
};

const doesArraysHaveSameElements = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    const sortedArr1 = [...arr1].sort((a, b) => a.localeCompare(b));
    const sortedArr2 = [...arr2].sort((a, b) => a.localeCompare(b));

    for (let i = 0; i < sortedArr1.length; i += 1) {
        if (sortedArr1[i] !== sortedArr2[i]) {
            return false;
        }
    }

    return true;
};

/**
 * Returns the arrayToSort sorted by colId (mutation). The choosen order is taken from columnsOrder.
 * @example
 * INPUT :
 * - arrayToSort = [{colId: '1', ...}, {colId: '2', ...}, {colId: '3', ...}]
 * - columnsOrder = ['3', '1', '2']
 *
 * OUTPUT :
 * - [{colId: '3', ...}, {colId: '2', ...}, {colId: '1', ...}]
 */
export const sortArrayFromColumnOrder = <T extends { field: string }[]>(
    arrayToSort: T,
    columnsOrder: string[] | null | undefined,
): T => {
    const isColumnsOrderUndefined = !columnsOrder || columnsOrder.length === 0;

    if (isColumnsOrderUndefined) return arrayToSort.reverse() as T;

    const haveColumnsOrderAndArrayToSortTheSameKeys = isColumnsOrderUndefined
        ? false
        : doesArraysHaveSameElements(
              arrayToSort.map((el) => el.field),
              columnsOrder,
          );

    if (!haveColumnsOrderAndArrayToSortTheSameKeys)
        return arrayToSort.reverse() as T;

    return arrayToSort.sort(
        (a, b) =>
            columnsOrder.findIndex((columnKey) => columnKey === a.field) -
            columnsOrder.findIndex((columnKey) => columnKey === b.field),
    );
};
